// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  CrewmemberLanguage,
} from 'common.data';
// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import {
  language,
} from 'common.language';
import {
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import CrewmemberFields from '../../fields/CrewmemberFields';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Types
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = Record<string, never>;

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'Crewmember'>>;
  loading: boolean;
};

const pageId = adminPages.adminCrewmembersPage;

class AdminCrewmembersPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'Crewmember') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyAdminCrewmember();

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    const newItem: $ModelUpsertParams<'Crewmember'> = {
      Birthdate: null,
      Email: '',
      Gender: null,
      Language: language.en as CrewmemberLanguage,
      Membership: null,
      Name: '',
      Phone: null,
      Qualification: null,
      RemoteImage: null,
      Tshirt: null,
      Weight: null,
    };

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          rightButtons={(
            <FormDialog
              fields={(
                <CrewmemberFields item={newItem} />
              )}
              id={'create-crewmember-field'}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$Model<'Crewmember'>>({
                    ...newItem,
                    ...values,
                  });

                  await Services.Api.upsertAdminCrewmember(params);
                },
              })}
              subTitle={t('New')}
              title={t('Crewmember')}
            >
              <Button
                color={'info'}
                id={'create-crewmember-button'}
                size={'sm'}
                title={t('Add new crewmember')}
              >
                {t('Add new')}
              </Button>
            </FormDialog>
            )}
          title={t('Crewmembers')}
        />
      </Card>
    );
  }

  renderButtons(dataItem: $Model<'Crewmember'>) {
    const {
      redirect,
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <Button
          className={'mr-1'}
          color={'secondary'}
          id={`redirect-admin-crewmembers-entries-page-button-${id}`}
          onClick={() => redirect({
            pathname: `/admin/crewmember/${id}/entries`,
          })}
          size={'sm'}
        >
          {t('Entries')}
        </Button>
        <Button
          className={'mr-1'}
          color={'secondary'}
          id={`redirect-admin-crewmembers-social-users-page-button-${id}`}
          onClick={() => redirect({
            pathname: `/admin/crewmember/${id}/social_users`,
          })}
          size={'sm'}
        >
          {t('Social users')}
        </Button>
        <Button
          className={'mr-1'}
          color={'secondary'}
          id={`redirect-admin-crewmembers-access-tokens-page-button-${id}`}
          onClick={() => redirect({
            pathname: `/admin/crewmember/${id}/access_tokens`,
          })}
          size={'sm'}
        >
          {t('Access tokens')}
        </Button>
        <FormDialog
          fields={(
            <CrewmemberFields item={dataItem} />
          )}
          id={`update-crewmember-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'Crewmember'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertAdminCrewmember(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Crewmember')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-crewmember-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-crewmember-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteAdminCrewmember(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Crewmember')}
        >
          <Button
            color={'danger'}
            id={`delete-crewmember-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Email')}</th>
            <th>{t('Phone')}</th>
            <th>{t('Date created')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'Crewmember'>) => (
            <tr key={dataItem.id}>
              <td>{dataItem.Name}</td>
              <td>{dataItem.Email}</td>
              <td>{dataItem.Phone}</td>
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminCrewmembersPageView;
